import { Feature } from 'src/WebApiController';

import {
  ColumnPersonalizationFlags,
  ReportColumnPersonalizationFlags,
} from '../columnUtils.types';
import {
  RecentSalesTableColumnId,
  SalesReportTableGroupingColumnId,
  SalesReportTableValueColumnId,
  SalesTableColumnId,
  SalesTableFlattenedColumnId,
} from './salesColumnUtils.types';

export const SALE_MAIN_COLUMNS: SalesTableColumnId[] = [
  SalesTableColumnId.Checkbox,
  SalesTableColumnId.Seating,
  SalesTableColumnId.QuantitySold,
  SalesTableColumnId.OrderDate,
  SalesTableColumnId.InHandDate,
  SalesTableColumnId.Status,
  SalesTableColumnId.TicketType,
  SalesTableColumnId.Proceeds,
  SalesTableColumnId.Sale,
];

export const SALE_MAIN_COLUMNS_V2: SalesTableColumnId[] = [
  SalesTableColumnId.Seating,
  SalesTableColumnId.QuantitySold,
  SalesTableColumnId.OrderDate,
  SalesTableColumnId.InHandDate,
  SalesTableColumnId.Status,
  SalesTableColumnId.TicketType,
  SalesTableColumnId.Proceeds,
  SalesTableColumnId.Sale,
];

export const SALE_TABLE_COLUMN_PERSONALIZATION_CONFIG_OVERRIDES: Record<
  SalesTableColumnId | SalesTableFlattenedColumnId | RecentSalesTableColumnId,
  Partial<ColumnPersonalizationFlags>
> = {
  [RecentSalesTableColumnId.Actions]: {
    isConfigurable: false,
    isRequired: true,
  },
  [RecentSalesTableColumnId.EventName]: {},
  [RecentSalesTableColumnId.Margin]: {},
  [RecentSalesTableColumnId.PandL]: {},
  [RecentSalesTableColumnId.Marketplace]: {},
  [RecentSalesTableColumnId.Proceeds]: {},
  [RecentSalesTableColumnId.SaleId]: {},
  [RecentSalesTableColumnId.Seating]: {},
  [RecentSalesTableColumnId.OrderDate]: { isDateTime: true },
  [RecentSalesTableColumnId.EventDate]: {},
  [RecentSalesTableColumnId.InHandDate]: {},
  [RecentSalesTableColumnId.Venue]: { isHiddenByDefault: true },
  [RecentSalesTableColumnId.Status]: {
    // Not actually numeric, but hack for numeric pills
    isNumeric: true,
    isEnumWithIcon: true,
  },

  [RecentSalesTableColumnId.SoldQuantityTicketType]: {},

  [SalesTableColumnId.Checkbox]: { isConfigurable: false },

  [SalesTableFlattenedColumnId.Event]: {
    isHiddenByDefault: false,
    // Not actually numeric, but hack for numeric pills
    isNumeric: true,
  },
  [SalesTableFlattenedColumnId.EventDate]: { isHiddenByDefault: true },
  [SalesTableFlattenedColumnId.Venue]: { isHiddenByDefault: true },
  [SalesTableFlattenedColumnId.Location]: { isHiddenByDefault: true },

  [SalesTableColumnId.Actions]: { isConfigurable: false, isRequired: true },
  [SalesTableColumnId.InHandDate]: {},
  [SalesTableColumnId.OrderDate]: { isDateTime: true },
  [SalesTableColumnId.Proceeds]: { isNumeric: true, isCurrency: true },
  [SalesTableColumnId.QuantitySold]: { isNumeric: true, isInteger: true },
  [SalesTableColumnId.Sale]: {},
  [SalesTableColumnId.Seating]: { isNumeric: true, isRequired: true },
  [SalesTableColumnId.Row]: {
    requiredFeatures: [Feature.RowSeatColumnSalePurchase],
  },
  [SalesTableColumnId.Seats]: {
    requiredFeatures: [Feature.RowSeatColumnSalePurchase],
  },
  [SalesTableColumnId.Status]: {
    // Not actually numeric, but hack for numeric pills
    isNumeric: true,
    isEnumWithIcon: true,
  },
  [SalesTableColumnId.TicketType]: {
    // Not actually numeric, but hack for numeric pills
    isNumeric: true,
    isEnumWithIcon: true,
  },
  [SalesTableColumnId.SoldTicketCost]: {
    isHiddenByDefault: true,
    isNumeric: true,
    isCurrency: true,
  },
  [SalesTableColumnId.Charges]: {
    isHiddenByDefault: true,
    isNumeric: true,
    isCurrency: true,
  },
  [SalesTableColumnId.Credits]: {
    isHiddenByDefault: true,
    isNumeric: true,
    isCurrency: true,
  },
  [SalesTableColumnId.PandL]: {
    isHiddenByDefault: true,
    isNumeric: true,
    isCurrency: true,
  },
  [SalesTableColumnId.Margin]: {
    isHiddenByDefault: true,
    isNumeric: true,
    isPercent: true,
  },
  [SalesTableColumnId.AutoFulfill]: {
    isHiddenByDefault: true,
    requiredFeatures: [Feature.AutoFulfillSetting],
  },
  [SalesTableColumnId.PaymentState]: {
    isHiddenByDefault: true,
  },
  [SalesTableColumnId.PaymentReceived]: {
    isHiddenByDefault: true,
  },
  [SalesTableColumnId.PaymentReferenceId]: {
    isHiddenByDefault: true,
  },
  [SalesTableColumnId.FulfillmentDate]: {
    isDateTime: true,
    isHiddenByDefault: true,
  },
  [SalesTableColumnId.PurchaseCreatedBy]: {
    isHiddenByDefault: true,
  },
  [SalesTableColumnId.PurchasedBy]: {
    isHiddenByDefault: true,
  },
  [SalesTableColumnId.PurchaseDate]: {
    isHiddenByDefault: true,
    isDateTime: true,
  },
  [SalesTableColumnId.VendorOrderId]: {
    isHiddenByDefault: true,
  },
  [SalesTableColumnId.VendorAccountEmail]: {
    isHiddenByDefault: true,
  },
  [SalesTableColumnId.OriginatedBy]: {
    isHiddenByDefault: true,
  },
  [SalesTableColumnId.TotalPaymentsAmount]: {
    isHiddenByDefault: true,
    requiredFeatures: [Feature.MarketplacePaymentConversion],
  },
  [SalesTableColumnId.TotalConvertedPaymentsAmount]: {
    isHiddenByDefault: true,
    requiredFeatures: [Feature.MarketplacePaymentConversion],
  },
  [SalesTableColumnId.WastedQuantity]: {
    isHiddenByDefault: true,
    requiredFeatures: [Feature.WasteSale],
  },
  [SalesTableColumnId.InternalNotes]: {
    isHiddenByDefault: true,
  },
  [SalesTableColumnId.SeatTraits]: {
    isHiddenByDefault: true,
    requiredFeatures: [Feature.SeatTraitsColumnSales],
  },
  [SalesTableColumnId.ConsigneeSellerAccountId]: {
    isHiddenByDefault: true,
    requiredFeatures: [Feature.EnableMasterUiViewForConsignors],
    consignorOnly: true,
  },
  [SalesTableColumnId.Marketplace]: {
    isHiddenByDefault: true,
    requiredFeatures: [Feature.SalesTableV2],
  },
} as const;

export const SALE_REPORT_V2_TABLE_COLUMN_PERSONALIZATION_CONFIG_OVERRIDES: Record<
  string,
  Partial<ReportColumnPersonalizationFlags>
> = {
  [SalesReportTableGroupingColumnId.IsNoFulfill]: {
    requiredFeatures: [Feature.AutoFulfillSetting],
  },
  [SalesReportTableGroupingColumnId.LocalEventDateTime]: {
    isDateTime: true,
  },
  [SalesReportTableGroupingColumnId.SaleDate]: {
    isDateTime: true,
  },
  [SalesReportTableGroupingColumnId.LastPaymentDate]: {
    isDateTime: true,
  },
  [SalesReportTableGroupingColumnId.CancellationDate]: {
    isDateTime: true,
  },
  [SalesReportTableGroupingColumnId.FulfillmentDate]: {
    isDateTime: true,
  },
  [SalesReportTableGroupingColumnId.PurchaseOrderDate]: {
    isDateTime: true,
  },
  [SalesReportTableValueColumnId.QuantitySold]: {
    isNumeric: true,
    isInteger: true,
  },
  [SalesReportTableValueColumnId.QuantityWasted]: {
    isNumeric: true,
    isInteger: true,
  },
  [SalesReportTableValueColumnId.TotalNetProceeds]: {
    isNumeric: true,
    isCurrency: true,
  },
  [SalesReportTableValueColumnId.TotalPaymentAmount]: {
    isNumeric: true,
    isCurrency: true,
  },
  [SalesReportTableValueColumnId.Charges]: {
    isNumeric: true,
    isCurrency: true,
  },
  [SalesReportTableValueColumnId.Credits]: {
    isNumeric: true,
    isCurrency: true,
  },
  [SalesReportTableValueColumnId.OutstandingBalance]: {
    isNumeric: true,
    isCurrency: true,
  },
  [SalesReportTableValueColumnId.PandL]: {
    isNumeric: true,
    isCurrency: true,
  },
  [SalesReportTableValueColumnId.TotalCost]: {
    isNumeric: true,
    isCurrency: true,
  },
  [SalesReportTableValueColumnId.Margin]: {
    isNumeric: true,
    isPercent: true,
  },
} as const;
